import React, { useEffect, useState } from 'react';
import { ExternalLink, Terminal } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { udfApi } from '../../../api/udfApi';

const FIELD_VALUE_CLASS = 'text-sm font-medium text-gray-700 dark:text-gray-300 leading-relaxed';

const StatPill = ({ children }) => (
  <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-[10px] tabular-nums text-gray-600 dark:bg-gray-800/80 dark:text-gray-400">
    {children}
  </span>
);

const formatSourceLabel = (source) => {
  if (!source) return '—';
  return String(source).replace(/_/g, ' ');
};

const FunctionPreview = ({ function: udf, onOpenDetails }) => {
  const [fullUdf, setFullUdf] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!udf) {
      setFullUdf(null);
      return;
    }

    const fetchFullUdf = async () => {
      setIsLoading(true);
      try {
        const data = await udfApi.getUDF(udf.id);
        setFullUdf(data);
      } catch (err) {
        console.error('Failed to fetch UDF details:', err);
        setFullUdf(udf);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFullUdf();
  }, [udf]);

  if (!udf) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-8">
        <div className="p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg mb-3">
          <Terminal size={24} className="text-gray-300 dark:text-gray-700" />
        </div>
        <h4 className="text-xs font-medium text-gray-400 dark:text-gray-600 uppercase tracking-wider mb-1">
          Select a function
        </h4>
        <p className="text-[10px] text-gray-400 dark:text-gray-600">
          Pick a function from the sidebar to preview
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-4 h-4 border-2 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin" />
          <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Loading...</span>
        </div>
      </div>
    );
  }

  const displayUdf = fullUdf || udf;
  const description =
    displayUdf.description || 'No description provided for this function.';
  const language = displayUdf.language || 'SQL';
  const source = formatSourceLabel(displayUdf.source);

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-950 transition-colors">
      <div className="flex flex-1 min-h-0 flex-col p-4">
        <p className={cn(FIELD_VALUE_CLASS, 'min-h-0 flex-1 overflow-y-auto custom-scrollbar')}>
          {description}
        </p>

        <div className="flex shrink-0 flex-wrap items-center gap-2 pt-3">
          <span
            className="min-w-0 max-w-[min(100%,14rem)] truncate text-[11px] font-semibold text-gray-900 dark:text-white"
            title={displayUdf.name}
          >
            {displayUdf.name}
          </span>
          <StatPill>{language}</StatPill>
          <StatPill>{source}</StatPill>
          <button
            type="button"
            onClick={onOpenDetails}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1',
              'text-[11px] font-semibold text-gray-500 transition-all',
              'hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700',
              'dark:border-gray-700 dark:text-gray-400 dark:hover:border-emerald-700 dark:hover:bg-emerald-900/20 dark:hover:text-emerald-300'
            )}
          >
            Details
            <ExternalLink size={12} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default FunctionPreview;
